import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, Copy, Eye, EyeOff, Cable, Download, ExternalLink, RefreshCw, Loader2, Rocket, ChevronDown, Terminal, KeyRound, MonitorSmartphone, Activity, XCircle, MinusCircle } from "lucide-react";
import api from "@/lib/api";

/* Bridge Wizard — interactive "connect your game" flow inside the Owner Deck.
   Step 1 credentials → Step 2 install & run → Step 3 live connection doctor
   (real calls against this server's key) → Step 4 player CEF surfaces.
   Release banner turns green when every critical system answers. */

const copyText = (t, label) => {
  const done = () => import("sonner").then(({ toast }) => toast.success(`${label} copied`));
  const fallback = () => {
    const ta = document.createElement("textarea");
    ta.value = t;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch { /* last resort */ }
    ta.remove();
    done();
  };
  try {
    if (!navigator.clipboard?.writeText) return fallback();
    navigator.clipboard.writeText(t).then(done).catch(fallback);
  } catch { fallback(); }
};

const snippetHdr = (key, sid) => `headers = { "X-NC-Key": "${key}" }   # from Step 1\nHUB = "${process.env.REACT_APP_BACKEND_URL}"\nSID = "${sid}"`;

const SYSTEM_META = [
  { id: "heartbeat", todo: "Bridge reports world state every 15s — powers the live dot, browser chips and loading screens", method: "POST", path: (sid) => `/api/nc/servers/${sid}/state`,
    snippet: (k, sid) => `${snippetHdr(k, sid)}\n# every 15 seconds:\nrequests.post(f"{HUB}/api/nc/servers/{SID}/state", headers=headers, json={\n  "hostname": "My RP Server", "players": 23, "max_players": 64,\n  "map": "Bornwood", "weather": "rain", "game_time": "23:41",\n  "on_duty": {"police": 6, "ems": 2, "fire": 1},\n  "roster": [{"playercode": 37049, "name": "Ada King", "job": "police", "ping": 41}]\n})` },
  { id: "config", todo: "Bridge pulls config every 30–60s — owner-deck edits (economy, ruleset, theme) apply live with no restart", method: "GET", path: (sid) => `/api/nc/servers/${sid}/config`,
    snippet: (k, sid) => `${snippetHdr(k, sid)}\ncfg = requests.get(f"{HUB}/api/nc/servers/{SID}/config", headers=headers).json()\n# apply cfg["economy"], cfg["ruleset"], cfg["theme"], cfg["factions"] in-game` },
  { id: "sessions", todo: "Mint a session token the moment a player opens a device — every CEF surface URL takes ?token=", method: "POST", path: (sid) => `/api/nc/servers/${sid}/session`,
    snippet: (k, sid) => `${snippetHdr(k, sid)}\nr = requests.post(f"{HUB}/api/nc/servers/{SID}/session", headers=headers,\n  json={"playercode": pc, "role": "police"}).json()   # role: police | ems | fire | judge | admin\nurl = f"{SITE}/cef/phone/{SID}?token=" + r["token"]    # open in CEF`,
    needsSite: true },
  { id: "banking", todo: "Push player accounts on join (and on change) — the phone banking app reads these", method: "POST", path: (sid) => `/api/nc/servers/${sid}/sync/accounts`,
    snippet: (k, sid) => `${snippetHdr(k, sid)}\nrequests.post(f"{HUB}/api/nc/servers/{SID}/sync/accounts", headers=headers, json=[\n  {"playercode": 37049, "name": "Ada King", "bank": 12400, "cash": 350}\n])` },
  { id: "phone", todo: "Nothing extra — handsets self-register on first open; calls & texts just work once sessions are minted", method: "GET", path: (sid) => `/api/nc/servers/${sid}/phone/me?token=…`, snippet: () => "# no bridge code needed\n# phone registers itself when the player opens it with a session token" },
  { id: "cad", todo: "Officers self-enrol when they open the MDT — just mint their session with role 'police'/'ems'/'fire'", method: "POST", path: (sid) => `/api/nc/servers/${sid}/session`, snippet: () => "# mint with a department role:\n# role 'police' -> Police Department, 'ems' -> EMS, 'fire' -> Fire & Rescue\n# rank ladder & departments are editable in the MDT (Dept > Structure)" },
  { id: "positions", todo: "Stream every on-duty unit's position every few seconds — the MDT map moves in real time", method: "POST", path: (sid) => `/api/nc/servers/${sid}/cad/units/positions`,
    snippet: (k, sid) => `${snippetHdr(k, sid)}\n# every 3-5 seconds, all units in one call:\nrequests.post(f"{HUB}/api/nc/servers/{SID}/cad/units/positions", headers=headers, json={\n  "positions": [{"playercode": 37049, "x": 120.5, "y": -340.2}, ...]\n})` },
  { id: "dispatch", todo: "Forward in-game 911 calls — the AI dispatcher summarizes, prioritizes and plots them on every MDT", method: "POST", path: (sid) => `/api/nc/servers/${sid}/911`,
    snippet: (k, sid) => `${snippetHdr(k, sid)}\nrequests.post(f"{HUB}/api/nc/servers/{SID}/911", headers=headers, json={\n  "transcript": "There's someone breaking into my store...!",\n  "caller": "Ada King", "caller_playercode": 37049,\n  "location_label": "24/7 Harbor", "x": 24.0, "y": -1346.0\n})` },
  { id: "court", todo: "Zero wiring — officers file charges from MDT incident reports; judges work the docket in the courthouse or the MDT Court tab", method: "GET", path: (sid) => `/api/nc/servers/${sid}/court/docket?token=…`, snippet: () => "# cases arrive from:  1) MDT incident reports with charges\n#                      2) POST /court/book if you arrest in-game directly" },
  { id: "dept", todo: "Zero wiring — departments, ranks, SOPs, laws, radio channels all auto-seed per server and edit in the MDT", method: "GET", path: (sid) => `/api/nc/servers/${sid}/cad/dept/config?token=…`, snippet: () => "# first officer to open the MDT seeds BPD/EMS/FD + penal code\n# command ranks are flagged in Dept > Structure (first chief bootstraps)" },
];

const SURFACES = [
  ["Main menu", "/cef/menu", "Startup & server browser — every player"],
  ["Loading screen", (sid) => `/cef/loading/${sid}`, "Shown while joining your server"],
  ["Phone OS", (sid) => `/cef/phone/${sid}?token=`, "Messages, VoIP, banking, market, 911 — needs session token"],
  ["Police MDT", (sid) => `/cef/cad/${sid}?token=`, "CAD, NCIC, court, dept ops — police/ems/fire sessions"],
  ["Courthouse", (sid) => `/cef/court/${sid}?token=`, "Docket + verdicts — judge-role sessions get the bench"],
  ["City hall", (sid) => `/cef/politics/${sid}?token=`, "Elections, mayor powers, policies"],
  ["Creator studio", (sid) => `/cef/creator/${sid}?token=`, "Recording, clips & stream overlays"],
  ["Admin console", (sid) => `/cef/admin/${sid}?token=`, "Staff only — admin sessions"],
];

const card = "border border-white/10 bg-[#0a0a0a] p-4 flex flex-col gap-2";
const stepTitle = "font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-100 flex items-center gap-2";

export default function BridgeWizard({ sid, deck }) {
  const site = window.location.origin;
  const key = deck.server.api_key || "";
  const [shown, setShown] = useState(false);
  const [health, setHealth] = useState(null);
  const [testing, setTesting] = useState(false);
  const [openSnip, setOpenSnip] = useState("");
  const [mysql, setMysql] = useState({ host: "", user: "root", password: "", database: "roleplay" });
  const [checks, setChecks] = useState(() => JSON.parse(localStorage.getItem(`nc_bridge_steps_${sid}`) || "[]"));

  const installerScript = () => `#!/usr/bin/env bash
# =============================================================================
# NEW CITIZEN — one-command bridge installer for ${deck.server.name || sid}
# Run on your game server host:   sudo bash nc-bridge-install.sh
# =============================================================================
set -euo pipefail
echo "==> Installing New Citizen bridge for ${sid}"

if ! command -v python3 >/dev/null 2>&1; then
  if command -v apt-get >/dev/null 2>&1; then apt-get update && apt-get install -y python3 python3-pip curl
  elif command -v yum >/dev/null 2>&1; then yum install -y python3 python3-pip curl
  else echo "Install Python 3.10+ manually, then re-run."; exit 1; fi
fi
python3 -m pip install --quiet requests pymysql

mkdir -p /opt/nc-bridge
curl -fsSL "${site}/bridge/nc_bridge.py" -o /opt/nc-bridge/nc_bridge.py

cat > /opt/nc-bridge/.env <<'ENVEOF'
NC_HUB=${process.env.REACT_APP_BACKEND_URL}
NC_SERVER_ID=${sid}
NC_API_KEY=${key}
# MySQL connector — uncomment and fill to sync banking/positions/licences
# straight from your server's MySQL (no game-code changes):
# NC_MYSQL_HOST=127.0.0.1
# NC_MYSQL_PORT=3306
# NC_MYSQL_USER=root
# NC_MYSQL_PASSWORD=your-password
# NC_MYSQL_DATABASE=roleplay
ENVEOF
chmod 600 /opt/nc-bridge/.env

cat > /etc/systemd/system/nc-bridge.service <<'UNITEOF'
[Unit]
Description=New Citizen Hub Bridge (${sid})
After=network-online.target
Wants=network-online.target

[Service]
WorkingDirectory=/opt/nc-bridge
ExecStart=/usr/bin/python3 /opt/nc-bridge/nc_bridge.py
Restart=always
RestartSec=5

[Install]
WantedBy=multi-user.target
UNITEOF

systemctl daemon-reload
systemctl enable --now nc-bridge
echo ""
echo "==> Probing your MySQL and testing the hub connection:"
python3 /opt/nc-bridge/nc_bridge.py --test || true
echo ""
echo "==> Bridge installed and running."
echo "    Re-probe table mappings after schema changes: python3 /opt/nc-bridge/nc_bridge.py --remap (then systemctl restart nc-bridge)"
echo "    Live logs:  journalctl -u nc-bridge -f"
echo "    Watch the Connection Doctor in your Owner Deck turn green."
`;

  const downloadInstaller = () => {
    const blob = new Blob([installerScript()], { type: "text/x-sh" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "nc-bridge-install.sh";
    a.click();
    URL.revokeObjectURL(a.href);
    import("sonner").then(({ toast }) => toast.success("Installer downloaded — run: sudo bash nc-bridge-install.sh"));
  };

  // starter pack: authenticated pre-filled downloads straight from the hub
  const dl = async (path, filename) => {
    const { toast } = await import("sonner");
    try {
      const r = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${filename} downloaded — credentials baked in`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed — verify ownership first");
    }
  };

  const runDoctor = useCallback(() => {
    if (!key) return;
    setTesting(true);
    api.get(`/nc/servers/${sid}/bridge/health`, { headers: { "X-NC-Key": key } })
      .then((r) => setHealth(r.data))
      .catch((e) => setHealth({ error: e.response?.data?.detail || "Key rejected — check Step 1" }))
      .finally(() => setTesting(false));
  }, [key, sid]);

  useEffect(() => { runDoctor(); const t = setInterval(runDoctor, 15000); return () => clearInterval(t); }, [runDoctor]);

  const toggleCheck = (id) => {
    const next = checks.includes(id) ? checks.filter((c) => c !== id) : [...checks, id];
    setChecks(next);
    localStorage.setItem(`nc_bridge_steps_${sid}`, JSON.stringify(next));
  };

  const byId = Object.fromEntries((health?.systems || []).map((s) => [s.id, s]));
  const okCount = (health?.systems || []).filter((s) => s.ok).length;
  const envBlock = `NC_HUB=${process.env.REACT_APP_BACKEND_URL}\nNC_SERVER_ID=${sid}\nNC_API_KEY=${key}`;

  return (
    <div className="flex flex-col gap-4" data-testid="bridge-wizard">
      {/* release banner */}
      <div className={`border p-4 flex items-center gap-4 ${health?.release_ready ? "border-nc-green/50 bg-nc-green/5" : "border-nc-orange/40 bg-nc-orange/5"}`} data-testid="release-banner">
        <Rocket className={`w-6 h-6 shrink-0 ${health?.release_ready ? "text-nc-green" : "text-nc-orange"}`} />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em]">
            {health?.release_ready ? "Release ready — players can connect" : health?.error ? "Connection test failed" : "Getting release-ready"}
          </p>
          <p className="font-mono text-[9px] text-zinc-400 mt-1">
            {health?.error || (health?.release_ready
              ? "Every critical system answers live. Optional systems below light up as your community uses them."
              : health ? `${health.blockers?.length || 0} critical step(s) left: ${(health.blockers || []).join(" · ")}` : "Running first connection test…")}
          </p>
        </div>
        {health && !health.error && (
          <span data-testid="doctor-score" className={`font-mono text-lg font-bold shrink-0 ${health.release_ready ? "text-nc-green" : "text-nc-orange"}`}>{okCount}/{health.systems.length}</span>
        )}
        <button data-testid="doctor-rerun" onClick={runDoctor} disabled={testing}
          className="shrink-0 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/15 px-3 py-2 text-zinc-300 hover:border-nc-orange hover:text-nc-orange transition-colors flex items-center gap-1.5 disabled:opacity-40">
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-test
        </button>
      </div>

      {/* STEP 1 — credentials */}
      <div className={card} data-testid="wizard-step-1">
        <p className={stepTitle}><KeyRound className="w-4 h-4 text-nc-orange" /> Step 1 — Your credentials</p>
        <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">Everything the bridge needs. The key is secret — never commit it or share it publicly.</p>
        {[["Hub API URL", process.env.REACT_APP_BACKEND_URL, "hub-url"], ["Server ID", sid, "server-id"]].map(([label, value, tid]) => (
          <div key={tid} className="flex items-center gap-2">
            <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 w-24 shrink-0">{label}</span>
            <code data-testid={`wizard-${tid}`} className="flex-1 min-w-0 font-mono text-[10px] text-zinc-200 bg-black border border-white/10 px-3 py-2 truncate">{value}</code>
            <button data-testid={`wizard-copy-${tid}`} onClick={() => copyText(value, label)} className="border border-white/15 p-2 text-zinc-400 hover:text-white transition-colors"><Copy className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 w-24 shrink-0">API key</span>
          <code data-testid="wizard-api-key" className="flex-1 min-w-0 font-mono text-[10px] text-zinc-200 bg-black border border-white/10 px-3 py-2 truncate">{shown ? key : "••••••••••••••••••••••••••••••••"}</code>
          <button data-testid="wizard-key-reveal" onClick={() => setShown((v) => !v)} className="border border-white/15 p-2 text-zinc-400 hover:text-white transition-colors">{shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
          <button data-testid="wizard-key-copy" onClick={() => copyText(key, "API key")} className="border border-white/15 p-2 text-zinc-400 hover:text-white transition-colors"><Copy className="w-3.5 h-3.5" /></button>
        </div>
        <button data-testid="wizard-copy-env" onClick={() => copyText(envBlock, ".env block")} className="w-fit font-mono text-[9px] uppercase tracking-[0.2em] border border-nc-blue/50 text-nc-blue px-3 py-2 hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">
          <Copy className="w-3 h-3" /> Copy all as .env
        </button>
      </div>

      {/* STEP 2 — install & run */}
      <div className={card} data-testid="wizard-step-2">
        <p className={stepTitle}><Terminal className="w-4 h-4 text-nc-orange" /> Step 2 — Install & run the bridge</p>
        <div className="border border-nc-green/30 bg-nc-green/[0.04] p-3.5 flex flex-col gap-2">
          <p className="font-mono text-[10px] font-bold text-nc-green uppercase tracking-[0.2em]">Easiest: one-command installer</p>
          <p className="font-mono text-[9px] text-zinc-400 leading-relaxed">Downloads the bridge, writes your credentials, and installs a systemd service that survives reboots. Run it on your server host:</p>
          <code className="font-mono text-[10px] text-zinc-100 bg-black border border-white/10 px-3 py-2">sudo bash nc-bridge-install.sh</code>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="wizard-installer-download" onClick={downloadInstaller} className="font-mono text-[9px] uppercase tracking-[0.2em] bg-nc-green text-black px-4 py-2.5 hover:bg-nc-green/90 transition-colors flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Download installer (credentials baked in)
            </button>
            <a data-testid="wizard-bridge-download" href="/bridge/nc_bridge.py" download className="font-mono text-[9px] uppercase tracking-[0.2em] text-nc-green border border-nc-green/40 px-3 py-2 hover:bg-nc-green/10 transition-colors flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Manual: nc_bridge.py
            </a>
            <button data-testid="wizard-bridge-prefilled" onClick={() => dl(`/nc/servers/${sid}/bridge/download`, `nc_bridge_${sid}.py`)}
              className="font-mono text-[9px] uppercase tracking-[0.2em] text-nc-orange border border-nc-orange/40 px-3 py-2 hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Starter pack: nc_bridge.py pre-filled
            </button>
            <button data-testid="wizard-env-prefilled" onClick={() => dl(`/nc/servers/${sid}/bridge/env`, `nc-bridge-${sid}.env`)}
              className="font-mono text-[9px] uppercase tracking-[0.2em] text-nc-orange border border-nc-orange/40 px-3 py-2 hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Starter pack: .env pre-filled
            </button>
          </div>
        </div>
        {/* MySQL connector */}
        <div className="border border-white/10 p-3.5 flex flex-col gap-2" data-testid="wizard-mysql">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-100">MySQL connector — zero game-code changes</p>
          <p className="font-mono text-[9px] text-zinc-400 leading-relaxed">
            Your server's own MySQL is the source of truth — the bridge connects directly, <strong className="text-zinc-200">auto-detects your actual table and column names</strong> (playercode vs citizenid, balance vs bank…) and syncs banking, cash, statements, live unit positions, the online roster and DMV re-tests. Fill these in — the installer then prints what it mapped and ready-made CREATE TABLE statements for anything missing:
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {[["NC_MYSQL_HOST", "host", "127.0.0.1"], ["NC_MYSQL_USER", "user", "root"], ["NC_MYSQL_PASSWORD", "password", "••••••"], ["NC_MYSQL_DATABASE", "database", "roleplay"]].map(([env, fld, ph]) => (
              <label key={env} className="flex items-center gap-1.5">
                <span className="font-mono text-[7.5px] uppercase tracking-[0.1em] text-zinc-500 w-28 shrink-0">{env}</span>
                <input data-testid={`mysql-${fld}`} type={fld === "password" ? "password" : "text"} value={mysql[fld]} placeholder={ph}
                  onChange={(e) => setMysql((m) => ({ ...m, [fld]: e.target.value }))}
                  className="flex-1 min-w-0 bg-black border border-white/15 px-2 py-1.5 font-mono text-[9.5px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange" />
              </label>
            ))}
          </div>
          <button data-testid="mysql-copy-env" onClick={() => copyText(`NC_MYSQL_HOST=${mysql.host || "127.0.0.1"}\nNC_MYSQL_PORT=3306\nNC_MYSQL_USER=${mysql.user}\nNC_MYSQL_PASSWORD=${mysql.password}\nNC_MYSQL_DATABASE=${mysql.database}`, "MySQL env block")}
            className="w-fit font-mono text-[8px] uppercase tracking-[0.2em] border border-white/15 text-zinc-300 px-2.5 py-1.5 hover:border-nc-green hover:text-nc-green transition-colors flex items-center gap-1.5">
            <Copy className="w-3 h-3" /> Copy MySQL env block (paste into /opt/nc-bridge/.env)
          </button>
          <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">The bridge probes your schema on first run and caches the mapping in nc_mapping.json (delete to re-probe, or run with --remap). Check what it found with <code className="text-zinc-400">python3 nc_bridge.py --test</code>.</p>
        </div>
        {[
          ["placed", "Bridge file placed where the server host can run it (needs outbound https + read access to your game state)"],
          ["env", "Credentials set — the .env block from Step 1 (NC_HUB / NC_SERVER_ID / NC_API_KEY)"],
          ["running", "Bridge process running — watch its console for the first heartbeat line"],
        ].map(([id, label]) => (
          <button key={id} data-testid={`wizard-check-${id}`} onClick={() => toggleCheck(id)} className="flex items-center gap-2.5 text-left group w-fit">
            {checks.includes(id) ? <CheckCircle2 className="w-4 h-4 text-nc-green shrink-0" /> : <Circle className="w-4 h-4 text-zinc-700 group-hover:text-zinc-500 shrink-0" />}
            <span className={`font-mono text-[10px] leading-snug ${checks.includes(id) ? "text-zinc-200" : "text-zinc-400"}`}>{label}</span>
          </button>
        ))}
        <div className="border border-white/10 bg-black p-3 font-mono text-[9px] text-zinc-500 leading-relaxed">
          <p className="text-zinc-600 uppercase tracking-[0.2em] text-[8px] mb-1">Expected console</p>
          <p className="text-nc-green">[bridge] config pulled — theme/economy/ruleset applied</p>
          <p className="text-nc-green">[bridge] state pushed — 23 players, 6 police on duty</p>
          <p className="text-zinc-400">[bridge] session minted for Ada King (phone open)</p>
        </div>
      </div>

      {/* STEP 3 — connection doctor */}
      <div className={card} data-testid="wizard-step-3">
        <div className="flex items-center gap-2">
          <p className={stepTitle}><Activity className="w-4 h-4 text-nc-orange" /> Step 3 — Connection doctor</p>
          <span className="ml-auto font-mono text-[8px] text-zinc-600 uppercase">{health?.checked_at ? `checked ${String(health.checked_at).slice(11, 19)} — auto 15s` : ""}</span>
        </div>
        <p className="font-mono text-[9px] text-zinc-500">Live tests against your server key. Cards flip green the moment the real traffic lands — wire systems in any order.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {SYSTEM_META.map((m) => {
            const s = byId[m.id];
            const ok = s?.ok;
            return (
              <div key={m.id} data-testid={`doctor-${m.id}`} className={`border p-3 flex flex-col gap-1.5 ${ok ? "border-nc-green/30 bg-nc-green/[0.03]" : s?.critical ? "border-nc-orange/30 bg-nc-orange/[0.03]" : "border-white/10"}`}>
                <div className="flex items-center gap-2">
                  {!s ? <MinusCircle className="w-3.5 h-3.5 text-zinc-600" /> : ok ? <CheckCircle2 className="w-3.5 h-3.5 text-nc-green" /> : s.critical ? <XCircle className="w-3.5 h-3.5 text-nc-orange" /> : <MinusCircle className="w-3.5 h-3.5 text-zinc-500" />}
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-100">{s?.label || m.id}</p>
                  {s?.critical && <span className="font-mono text-[7px] uppercase border border-nc-orange/40 text-nc-orange px-1 py-0.5">required</span>}
                  <button data-testid={`doctor-snip-${m.id}`} onClick={() => setOpenSnip(openSnip === m.id ? "" : m.id)} className="ml-auto text-zinc-500 hover:text-nc-blue transition-colors flex items-center gap-1 font-mono text-[8px] uppercase">
                    example <ChevronDown className={`w-3 h-3 transition-transform ${openSnip === m.id ? "rotate-180" : ""}`} />
                  </button>
                </div>
                <p className="font-mono text-[8.5px] text-zinc-500 leading-snug">{m.todo}</p>
                {s && <p data-testid={`doctor-detail-${m.id}`} className={`font-mono text-[8px] ${ok ? "text-nc-green" : "text-zinc-400"}`}>{s.detail}</p>}
                <button data-testid={`doctor-copy-${m.id}`} onClick={() => copyText(`${m.method} ${process.env.REACT_APP_BACKEND_URL}${m.path(sid)}`, "endpoint")}
                  className="w-fit font-mono text-[8px] text-nc-blue border border-nc-blue/30 px-2 py-1 hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">
                  <span className="font-bold">{m.method}</span> {m.path(sid)} <Copy className="w-2.5 h-2.5" />
                </button>
                {openSnip === m.id && (
                  <pre data-testid={`doctor-snippet-${m.id}`} className="border border-white/10 bg-black p-2.5 font-mono text-[8.5px] text-zinc-300 whitespace-pre-wrap leading-relaxed overflow-x-auto">{m.snippet(key || "<API_KEY>", sid).replaceAll("{SITE}", site)}</pre>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* STEP 4 — player surfaces */}
      <div className={card} data-testid="wizard-step-4">
        <p className={stepTitle}><MonitorSmartphone className="w-4 h-4 text-nc-orange" /> Step 4 — Player surfaces (CEF)</p>
        <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">Open these in the in-game browser. Anything with <code className="text-zinc-300">?token=</code> takes a session from Step 3's <code className="text-zinc-300">/session</code> call. Preview opens the demo floor in a new tab.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SURFACES.map(([name, pathFn, note]) => {
            const path = typeof pathFn === "function" ? pathFn(sid) : pathFn;
            const preview = `${site}${path}${path.includes("token=") ? "" : ""}`.replace(/\?token=$/, "");
            return (
              <div key={name} data-testid={`surface-${name.toLowerCase().replace(/ /g, "-")}`} className="border border-white/10 p-3 flex flex-col gap-1.5">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-100">{name}</p>
                <p className="font-mono text-[8.5px] text-zinc-500 leading-snug flex-1">{note}</p>
                <div className="flex gap-1.5">
                  <a data-testid={`surface-preview-${name.toLowerCase().replace(/ /g, "-")}`} href={preview} target="_blank" rel="noopener noreferrer"
                    className="font-mono text-[8px] uppercase border border-white/15 text-zinc-300 px-2 py-1.5 hover:border-nc-blue hover:text-nc-blue transition-colors flex items-center gap-1">
                    <ExternalLink className="w-2.5 h-2.5" /> Preview
                  </a>
                  <button data-testid={`surface-copy-${name.toLowerCase().replace(/ /g, "-")}`} onClick={() => copyText(`${site}${path}<token>`, `${name} URL`)}
                    className="font-mono text-[8px] uppercase border border-white/15 text-zinc-300 px-2 py-1.5 hover:border-nc-green hover:text-nc-green transition-colors flex items-center gap-1">
                    <Copy className="w-2.5 h-2.5" /> In-game URL
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <p className="font-mono text-[8.5px] text-zinc-600 leading-relaxed border-t border-white/5 pt-2">
          Rule of thumb: mint a fresh token each time a player opens a device (they last 15 min), and never expose your API key to clients — only the bridge holds it.
        </p>
      </div>
    </div>
  );
}
